import { getUuid } from '@sibe/utilities/common';
import { HsRenderObjects } from '../../engine-new/filtering/render-objects.class';
import type { HsPerspectiveCamera } from '../../engine-new/objects/cameras/perspective-camera.class';
import { HsGraphics } from '../../graphic-render-new/graphics.class';
import { HsTextureMaterial } from '../../graphic-render-new/materials/texture-material.class';
import { HsGraphicLayer } from '../../graphic-render-new/objects/graphic-layer.class';
import type { HsGraphicObject } from '../../graphic-render-new/objects/graphic-object.class';
import { HsGraphicQueue } from '../../graphic-render-new/objects/graphic-queue.class';
import type { HsRender } from '../../graphic-render-new/render.class';
import type { HsShader2D } from '../../graphic-render-new/shaders/classes/shader-2d.class';
import { HsShaderData2D } from '../../graphic-render-new/shaders/classes/shader-data-2d.class';
import { HsShaderObjectType } from '../../graphic-render-new/shaders/enums/shader-object-type.enum';
import { HsCanvasSize } from '../../tools-new/utils/canvas-size.class';

export interface HsViewerSurfaceOptions {
  /** MSAA samples of the scene layer; 0 turns multisampling off. */
  antialiasingSamples: 0 | 2 | 4 | 8;
}

/**
 * The WebGL side of a viewer canvas: graphics, the scene layer with its queue and visible objects, and
 * the canvas size. It sizes the canvas to its CSS box and keeps the camera aspect, the layers and the
 * viewport in step when the box changes.
 */
export class HsViewerSurface {
  public readonly graphics: HsGraphics;
  public readonly objectsLayer: HsGraphicLayer;
  public readonly objectsQueue: HsGraphicQueue;
  public readonly renderObjects: HsRenderObjects<HsGraphicObject, HsGraphicLayer>;
  public readonly canvasSize: HsCanvasSize;
  /** CSS size of the canvas box, unrounded (`canvasSize` rounds and caps it). */
  public width: number;
  public height: number;

  /** Sets up rendering on the canvas; `render` gets its default layer material from the surface. */
  public constructor(
    public readonly canvas: HTMLCanvasElement,
    public readonly gl: WebGL2RenderingContext,
    private readonly camera: HsPerspectiveCamera,
    render: HsRender,
    options: HsViewerSurfaceOptions
  ) {
    // Focusable, so keyboard navigation reaches the canvas.
    canvas.tabIndex = 0;
    const shader2DData: HsShaderData2D = new HsShaderData2D(HsShaderObjectType.single);
    shader2DData.addTexture();
    const material2D: HsTextureMaterial<HsShader2D> = new HsTextureMaterial(gl, shader2DData.makeShader());
    this.graphics = new HsGraphics(gl);
    render.init(this.graphics, material2D);

    this.objectsLayer = new HsGraphicLayer(camera, gl, options.antialiasingSamples);
    this.objectsQueue = new HsGraphicQueue(getUuid(), this.objectsLayer);
    this.renderObjects = new HsRenderObjects(this.objectsLayer);

    const { top, left, width, height }: DOMRect = canvas.getBoundingClientRect();
    this.width = width;
    this.height = height;
    canvas.width = width;
    canvas.height = height;
    this.objectsLayer.resizeFramebuffer(gl, canvas.width, canvas.height);
    this.canvasSize = new HsCanvasSize(width, height, top, left);
    camera.aspect = width / height;
    gl.viewport(0, 0, width, height);
  }

  /**
   * Re-reads the canvas box after a resize: updates the canvas size, the camera aspect and projection,
   * and resizes the scene layer and `extraLayers` through `render`.
   */
  public resize(render: HsRender, extraLayers: readonly HsGraphicLayer[] = []): HsCanvasSize {
    const { top, left, width, height }: DOMRect = this.canvas.getBoundingClientRect();
    this.width = width;
    this.height = height;
    this.canvasSize.realWidth = width;
    this.canvasSize.realHeight = height;
    this.canvasSize.renderWidth = width;
    this.canvasSize.renderHeight = height;
    this.canvasSize.aspect = width / height;
    this.canvasSize.top = top;
    this.canvasSize.left = left;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    render.setRenderSize(this.gl, width, height, [this.objectsLayer, ...extraLayers]);
    return this.canvasSize;
  }
}
